/**
 * Static map of workshop-slug redirects.
 *
 * When a visitor hits `/workshops/<old-slug>`, the server returns a 301 to
 * `/workshops/<new-slug>`, and any checkout attempt against the old slug is
 * rejected.
 *
 * Two kinds of entries live here:
 *   1. **Series-slug renames** — old canonical series slugs → new canonical
 *      series slugs (e.g., "claude-sales" → "claude-for-sales-teams-workshop").
 *      These are matched as a PREFIX by getEventRedirect() so dated session
 *      slugs (claude-sales-may-26-2026) also redirect correctly.
 *   2. **One-off slug overrides** — specific past sessions whose slugs were
 *      hardcoded in external links.
 *
 * Cloudflare 301s can layer on top of this for SEO (so the redirect happens
 * before the request hits Cloud Run). When a CF rule exists for a slug,
 * remove the matching entry below — keeping both layers in sync.
 */

/**
 * Series-level slug renames (May 2026 launch consistency pass).
 *
 * These get matched as a prefix — any slug starting with the OLD value gets
 * its prefix swapped for the NEW value, preserving any date suffix.
 *
 * Example: "claude-cowork-workshop-may-21-2026" hits the cowork prefix and
 * redirects to "claude-cowork-for-beginners-workshop-may-21-2026".
 */
export const SERIES_SLUG_RENAMES: Record<string, string> = {
  "claude-cowork-workshop":     "claude-cowork-for-beginners-workshop",
  "claude-code-workshop":       "claude-code-for-beginners-workshop",
  "claude-sales":               "claude-for-sales-teams-workshop",
  // Renamed twice: fundamentals-of-ai-workshop → ai-fundamentals-workshop
  // (May 2026), then → the-ai-first-leader-workshop (2026-06-12, workshop
  // repositioned as The AI-First Leader). getEventRedirect() is single-pass,
  // so both old prefixes point directly at the current slug.
  "fundamentals-of-ai-workshop": "the-ai-first-leader-workshop",
  "ai-fundamentals-workshop":    "the-ai-first-leader-workshop",
};

/**
 * Exact-match slug → slug redirects for one-off cases that don't follow a
 * series prefix pattern (e.g., a single past session slug).
 */
export const EVENT_REDIRECTS: Record<string, string> = {
  // Old slugs that previewed during development. Kept here so any link that
  // escaped before the slug was finalized doesn't 404.
  "fundamentals-of-ai-june-17-2026":          "the-ai-first-leader-workshop",
  "fundamentals-of-ai-workshop-june-17-2026": "the-ai-first-leader-workshop",

  // Past Claude Code session — its slug was hardcoded as a CTA in ~22 blog
  // posts (now repointed in DB). External citations / old emails / search
  // snippets may still link to it; this redirect sends them to the live
  // series page instead of the "event ended" detail page.
  "learn-claude-code-for-beginners":   "claude-code-for-beginners-workshop",
  "claude-code-may-2026":              "claude-code-for-beginners-workshop",

  // "claude-code" was the canonical series slug before the May 2026 rename
  // to "claude-code-for-beginners-workshop". Cannot use SERIES_SLUG_RENAMES
  // prefix matching (would loop on the new canonical slug), so this is a
  // pure exact-match override for the bare series URL.
  "claude-code":                       "claude-code-for-beginners-workshop",

  // June 3 → June 18 single-date consolidation (2026-05-20). The series was
  // collapsed to one session and its event slug renamed from the dated slug to
  // the bare series slug, so the dated URL no longer exists. Redirect old
  // confirmation links / cached references to the live series page.
  "claude-code-for-beginners-workshop-june-3-2026": "claude-code-for-beginners-workshop",

  // AI Agent Architecture Workshop (Spiros) — pulled from the lineup
  // 2026-05-18. Any inbound links land on the workshops index.
  "ai-architecture-workshop":          "",

  // GSC 404 (flagged 2026-07-18): /workshops/claude-code-for-teens was never a
  // live slug — the shipped product is "claude-code-for-kids" (verified 200).
  // Point the mistyped/anticipated teens URL at the live kids page.
  "claude-code-for-teens":             "claude-code-for-kids",
};

/**
 * Resolve a slug to its redirect target. Returns:
 *   - The new slug if a prefix or exact-match rule applies.
 *   - `""` for slugs that should be sent to the workshops index (no detail
 *     page exists for them anymore).
 *   - `null` if no redirect rule applies and the request should fall through
 *     to the normal event lookup.
 */
export function getEventRedirect(slug: string): string | null {
  // Exact-match takes priority over prefix
  if (slug in EVENT_REDIRECTS) {
    return EVENT_REDIRECTS[slug];
  }
  for (const [oldPrefix, newPrefix] of Object.entries(SERIES_SLUG_RENAMES)) {
    if (slug === oldPrefix) return newPrefix;
    if (slug.startsWith(oldPrefix + "-")) {
      return newPrefix + slug.slice(oldPrefix.length);
    }
  }
  return null;
}
